import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { apiErr } from "../../lib/utils";
import { Button } from "../ui/button";
import { Input, Textarea } from "../ui/input";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Plus, RefreshCw, ExternalLink, Sparkles, Check, X, ShieldAlert, Trash2 } from "lucide-react";
import { toast } from "sonner";

function AddSourceDialog({ onAdded }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ bank_name: "", source_url: "", source_type: "product_page", retrieval_frequency: "quarterly" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.bank_name || !f.source_url) return toast.error("Bank and URL required");
    setBusy(true);
    try { await api.post("/green-finance/sources", f); setOpen(false); setF({ bank_name: "", source_url: "", source_type: "product_page", retrieval_frequency: "quarterly" }); onAdded(); toast.success("Source added"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="add-source-btn"><Plus className="h-4 w-4" /> Add bank source</Button>
      <DialogContent className="max-w-lg" data-testid="add-source-dialog">
        <DialogHeader><DialogTitle className="font-display">Add approved bank source</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div><label className="text-[10px] text-muted-foreground">Bank name</label><Input value={f.bank_name} onChange={(e) => setF({ ...f, bank_name: e.target.value })} data-testid="source-bank" /></div>
          <div><label className="text-[10px] text-muted-foreground">Source URL (official page)</label><Input value={f.source_url} onChange={(e) => setF({ ...f, source_url: e.target.value })} placeholder="https://" data-testid="source-url" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-[10px] text-muted-foreground">Source type</label>
              <Select value={f.source_type} onValueChange={(v) => setF({ ...f, source_type: v })}><SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>{["product_page", "pdf", "faq", "framework", "press_release"].map((t) => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select></div>
            <div><label className="text-[10px] text-muted-foreground">Retrieval frequency</label>
              <Select value={f.retrieval_frequency} onValueChange={(v) => setF({ ...f, retrieval_frequency: v })}><SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>{["monthly", "quarterly", "manual"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="flex justify-end"><Button variant="primary" size="sm" onClick={submit} disabled={busy} data-testid="source-submit">{busy ? "Adding…" : "Add source"}</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function BankSourcesTab() {
  const [sources, setSources] = useState([]);
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/green-finance/sources").then((r) => { setSources(r.data.sources); setPending(r.data.pending_extractions); });
  useEffect(() => { load(); }, []);

  const extract = async (id) => {
    setBusy(id);
    try { const r = await api.post(`/green-finance/sources/${id}/extract`); toast[r.data.fetched ? "success" : "message"](r.data.fetched ? "Extracted — review the draft below" : "Could not fetch the page; placeholder draft created for review"); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };
  const approve = async (id, idx) => { try { const r = await api.post(`/green-finance/extractions/${id}/approve`, { offer_index: idx }); toast.success(`Published ${r.data.count} offer(s) to catalog`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const reject = async (id) => { try { await api.post(`/green-finance/extractions/${id}/reject`); toast.success("Extraction rejected"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const delSource = async (id) => { try { await api.delete(`/green-finance/sources/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="space-y-4" data-testid="gf-sources-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="rounded-sm border border-border bg-surface-hover/40 p-2.5 text-[11px] text-muted-foreground flex gap-2 flex-1">
          <ShieldAlert className="h-3.5 w-3.5 shrink-0 mt-0.5 text-[#FFB800]" />
          The AI agent only reads approved bank URLs. Extracted offers are drafts and require human review before they appear in the catalog. We never invent rates.
        </div>
        <AddSourceDialog onAdded={load} />
      </div>

      <div className="space-y-2">
        {sources.map((s) => (
          <div key={s.id} className="card-flat p-3 flex items-center gap-3 flex-wrap" data-testid={`source-${s.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm">{s.bank_name} <Badge variant="outline" className="text-[8px]">{s.source_type?.replace(/_/g, " ")}</Badge></div>
              <a href={s.source_url} target="_blank" rel="noreferrer" className="text-[11px] text-accent hover:underline inline-flex items-center gap-1 truncate">{s.source_url} <ExternalLink className="h-3 w-3" /></a>
              <div className="text-[10px] text-muted-foreground">Freq: {s.retrieval_frequency} · Last retrieved: {s.last_retrieved ? new Date(s.last_retrieved).toLocaleString() : "never"}{s.extraction_confidence != null ? ` · confidence ${Math.round(s.extraction_confidence * 100)}%` : ""}</div>
            </div>
            <Button size="sm" variant="primary" disabled={busy === s.id} onClick={() => extract(s.id)} data-testid={`extract-${s.id}`}><RefreshCw className={`h-3.5 w-3.5 ${busy === s.id ? "animate-spin" : ""}`} /> {busy === s.id ? "Extracting…" : "Run AI extraction"}</Button>
            <Button size="sm" variant="ghost" onClick={() => delSource(s.id)}><Trash2 className="h-3.5 w-3.5 text-muted-foreground" /></Button>
          </div>
        ))}
        {!sources.length && <div className="text-sm text-muted-foreground py-4">No bank sources yet. Add an approved official URL to begin.</div>}
      </div>

      {pending.length > 0 && (
        <div>
          <div className="label-eyebrow mb-2 inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-accent" /> Pending review ({pending.length})</div>
          <div className="space-y-3">
            {pending.map((e) => (
              <div key={e.id} className="card-flat p-3" data-testid={`extraction-${e.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="text-sm font-medium">{e.bank_name} <span className="text-[10px] text-muted-foreground">· {e.fetched ? "fetched" : "fetch failed"} · confidence {Math.round((e.extraction_confidence || 0) * 100)}%</span></div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="primary" onClick={() => approve(e.id, null)} data-testid={`approve-extraction-${e.id}`}><Check className="h-3.5 w-3.5" /> Approve all</Button>
                    <Button size="sm" variant="outline" onClick={() => reject(e.id)} data-testid={`reject-extraction-${e.id}`}><X className="h-3.5 w-3.5" /> Reject</Button>
                  </div>
                </div>
                <div className="text-[10px] text-muted-foreground italic mt-1">This offer summary was generated from public source content and requires review before use.</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                  {(e.offers || []).map((o, idx) => (
                    <div key={idx} className="border border-border rounded-sm p-2 text-xs">
                      <div className="font-medium">{o.offer_name} <Badge variant="outline" className="text-[8px]">{o.offer_category}</Badge></div>
                      <div className="text-muted-foreground mt-1">{o.eligible_use_of_proceeds}</div>
                      <div className="text-[10px] text-muted-foreground mt-1">Rate: {o.indicative_interest_rate != null ? `${o.indicative_interest_rate}%` : "not published"} ({o.interest_rate_source})</div>
                      <Button size="sm" variant="ghost" className="mt-1 text-accent h-6 px-2" onClick={() => approve(e.id, idx)}>Publish this only</Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
